import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Search, Truck, ShieldCheck, Zap } from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/1f681ca5-b54b-40cf-9539-84fe176a321c/images/b050d9f07857eaddcc3fb26190921e241dd9133fd96fbe3dfc4a607b606675d4.png";

export default function Landing() {
    const navigate = useNavigate();
    const [code, setCode] = useState("");

    function submit(e) {
        e.preventDefault();
        const v = code.trim();
        if (!v) return;
        navigate(`/track/${encodeURIComponent(v)}`);
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Top bar */}
            <header className="border-b border-slate-200">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
                    <Link
                        to="/"
                        className="flex items-center gap-2 group"
                        data-testid="brand-home-link"
                    >
                        <div className="relative">
                            <div className="w-7 h-7 bg-[var(--tp-blue)]" />
                            <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-[var(--tp-orange)]" />
                        </div>
                        <span className="font-black tracking-tight text-lg">
                            TRACKING<span className="text-[var(--tp-orange)]">PLUS</span>
                        </span>
                    </Link>
                    <nav className="flex items-center gap-3 text-sm">
                        <a
                            href="#como-funciona"
                            className="hidden md:inline text-slate-600 hover:text-slate-900"
                        >
                            Como funciona
                        </a>
                        <Link
                            to="/admin/login"
                            data-testid="backoffice-link"
                            className="px-4 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider hover:bg-slate-50 transition"
                        >
                            Backoffice
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="relative tp-grain overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[var(--tp-orange)] mb-6">
                            <Zap className="w-3.5 h-3.5" />
                            Logística inteligente
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900">
                            Acompanhe a sua{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10">encomenda</span>
                                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[var(--tp-orange)]/30 -z-0" />
                            </span>{" "}
                            com precisão.
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 max-w-xl">
                            Insira o seu número de tracking e saiba onde está o seu envio em
                            tempo real. Atualizações claras, sem segredos.
                        </p>

                        <form
                            onSubmit={submit}
                            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl"
                            data-testid="hero-track-form"
                        >
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    data-testid="tracking-input"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="Ex.: TP-AB12CD"
                                    className="w-full h-14 pl-12 pr-4 text-lg font-mono border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none rounded-none bg-white"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="track-button"
                                className="h-14 px-7 bg-[var(--tp-orange)] hover:bg-[var(--tp-orange-hover)] text-white font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5"
                            >
                                Localizar
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </form>

                        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-[var(--tp-blue)]" /> Dados
                                seguros
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Truck className="w-4 h-4 text-[var(--tp-blue)]" /> NACEX · CTT
                                · DPD · CORREOS · OUTRA
                            </span>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[var(--tp-blue)]/10" />
                            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[var(--tp-orange)]/10" />
                            <img
                                src={HERO_IMG}
                                alt="Encomenda em alta velocidade"
                                className="relative w-full max-w-md mx-auto"
                            />
                            <div className="absolute top-1/2 -left-10 right-0 h-1 bg-[var(--tp-orange)]/40 tp-speed pointer-events-none" />
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="como-funciona" className="border-t border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                            Como funciona
                        </span>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-black tracking-tight">
                            Três passos. Zero confusão.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {[
                            {
                                n: "01",
                                t: "Nº Encomenda",
                                d: "Copia o número da tua encomenda da loja online onde efetuaste a compra.",
                            },
                            {
                                n: "02",
                                t: "Insira no localizador",
                                d: "Coloque o número da encomenda no campo acima e clique em Localizar.",
                            },
                            {
                                n: "03",
                                t: "Veja em tempo real",
                                d: "Acompanhe o estado do envio passo a passo até à entrega.",
                            },
                        ].map((s) => (
                            <div key={s.n} className="bg-white p-8">
                                <div className="font-mono text-sm text-[var(--tp-orange)] font-bold">
                                    {s.n}
                                </div>
                                <h3 className="mt-4 text-xl font-bold tracking-tight">{s.t}</h3>
                                <p className="mt-2 text-slate-600">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <footer className="border-t border-slate-200 py-8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
                    <span>
                        © {new Date().getFullYear()} TRACKING PLUS — Logística Plus
                    </span>
                    <span className="font-mono uppercase tracking-widest text-xs">
                        Powered by LOGISTICA PLUS
                    </span>
                </div>
            </footer>
        </div>
    );
}
